import React, { useState } from "react"

import dayjs from "dayjs"
import _, { truncate } from "lodash"
import PropTypes from "prop-types"
import ReactDOM from "react-dom"
import { connect } from "react-redux"

import { RemoveSuggestionBtn } from "@components/common"
import { GAMEPLAN_TYPES } from "@constants/gameplan"
import { selectActiveTeeup, selectActiveTeeupId } from "@selectors/activeTeeup"
import { selectGuestLinkedCalendar } from "@selectors/calendar"
import { selectTeeupPeople } from "@selectors/teeups"
import { selectUserInfo, selectToken } from "@selectors/user"
import { locationTypeIds } from "@utils/gamePlanUtils"
import { MAX_CREATOR_NAME_LENGTH } from "@utils/suggestionUtils"

import { useLabelNew, useSuggestionData } from "../NewTeeupGameplan/hooks"
import SuggestionPopup from "../gamePlansCard/SuggestionPopup"
import { PreviewSuggestionReactions, CreatorAvatar } from "components"

import { SuggestionLabel, StatusLabel } from "./atoms"
import { useValue } from "./hooks"
import {
  OutsideWrapper,
  Wrapper,
  MainInfo,
  Image,
  CreatorName,
  CreatorNameCollapsed,
  StarBadge,
} from "./suggestionItemStyles"

const SuggestionItem = props => {
  const {
    isCollapsed,
    isPopup,
    suggestion,
    suggestionsLength,
    userInfo,
    teeupId,
    activeTeeup,
    people,
    isPreview = false,
    isCreateTeeup = false,
    isReacted,
    linkedGuestCalendar,
    openSuggestionPreview,
    isAuthenticated,
    children,
    isDisableOnClick,
    contentClassName,
    wrapperClassName,
  } = props

  const [isSuggestionPopupOpen, setIsSuggestionPopupOpen] = useState(false)
  const { img, value, details } = useSuggestionData(suggestion)
  const isDecided = suggestion.selected || suggestion.isSelected
  const isDecidedInPopup = isPopup && isDecided

  const detailsValue = details || "No link provided"
  const isWithdrawn = suggestion.isWithdrawn
  const isPast =
    dayjs().isAfter(dayjs(suggestion.startDate)) && !isWithdrawn && !isDecided

  const isMap =
    suggestion.type === "where" &&
    suggestion?.locationType === locationTypeIds.realWorld

  const { renderValues } = useValue({
    isPopup,
    isDecided,
    detailsValue,
    isCollapsed,
    value,
    isDecidedInPopup,
    isMap,
  })

  const isLabelNew = useLabelNew({
    userInfo,
    suggestion,
    teeupId,
    isPreview,
  })

  const toggleSuggestionPopupHandle = e => {
    if (isDisableOnClick) return

    if (isPreview) {
      if (isAuthenticated || !openSuggestionPreview) return

      return openSuggestionPreview(suggestion.id, suggestion.isCreated)
    } else if (!isPopup) {
      // Stop the opening click from reaching document -- OutsideClickHandler
      // inside the newly-mounted SuggestionPopup can attach its listener
      // synchronously within this same click's bubble phase, which would
      // otherwise treat the click that opened the popup as an "outside"
      // click and close it immediately (flash-and-close).
      if (typeof e?.stopPropagation === "function") e.stopPropagation()
      setIsSuggestionPopupOpen(prevState => !prevState)
    }
  }

  const creatorName = `@${suggestion.creator?.name}`
  const creatorAvatar = suggestion.creator?.avatar || userInfo?.avatar
  // QJ refinement (2026-07-04, revises the 8b no-votes-ever ruling): thumbs
  // ARE the weigh-in affordance while a suggestion is undecided, and are
  // hidden once decided (irrelevant after a decision) -- restored, no longer
  // gated to isPreview-only. isReacted/isCollapsed unchanged (still governs
  // whether the collapsed row shows its own reaction state).
  const isPreviewReactions =
    (!isCollapsed || isReacted) && suggestion.reactions && !isDecided
  const isRemoveBtn =
    isCreateTeeup ||
    (suggestion.isCreated && !suggestion.isPublished && isCollapsed)

  const renderStatusLabel = () => {
    let labelText = _.capitalize(suggestion.type)

    if (isLabelNew) {
      labelText += " · New Suggestion"
    } else if (isWithdrawn) {
      labelText += " · Withdrawn"
    }
    // QJ: no "Decided" badge/label anywhere in the build -- a decided
    // suggestion just shows its value, no status-label suffix.

    return (
      <StatusLabel
        isDecided={isDecided}
        text={labelText}
        type={suggestion.type}
      />
    )
  }

  const renderConflictLabel = () => {
    const conflictStatusText =
      !!linkedGuestCalendar && suggestion.isCalendarConflict
        ? "Calendar conflict"
        : "Available"
    const conflictStatusColor =
      !!linkedGuestCalendar && suggestion.isCalendarConflict
        ? "var(--red2)"
        : "var(--green2)"

    // C1 fix (2026-07-03): the row-level "Calendar conflict"/"Available" label was gated on
    // linkedGuestCalendar (the GUEST-preview selector) only -- logged-in users never saw it, on
    // either the collapsed Game Plan list rows or the expanded popup row. useGameplans.js now
    // computes isCalendarConflict for every When suggestion for logged-in users (decided or
    // pending, checked against sibling suggestions + the user's synced calendar events -- see
    // src/components/NewTeeupGameplan/hooks/useGameplans.js). Show the same label keyed off
    // suggestion.isCalendarConflict being defined, rather than the guest-only calendar link.
    // Guest-preview path (isPreview) is untouched.
    const isLoggedInConflictAware =
      !isPreview &&
      !isCreateTeeup &&
      suggestion.type === GAMEPLAN_TYPES.when &&
      suggestion.isCalendarConflict !== undefined

    const loggedInConflictText = suggestion.isCalendarConflict
      ? "Calendar conflict"
      : "Available"
    const loggedInConflictColor = suggestion.isCalendarConflict
      ? "var(--red2)"
      : "var(--green2)"

    switch (true) {
      case isPreview && isCollapsed && !isCreateTeeup:
        return (
          <SuggestionLabel
            text={truncate(creatorName, {
              length: MAX_CREATOR_NAME_LENGTH,
              separator: "...",
            })}
          />
        )
      case isPreview && !isCollapsed && !!linkedGuestCalendar:
        return (
          <SuggestionLabel
            isDot
            color={conflictStatusColor}
            text={conflictStatusText}
          />
        )
      case isLoggedInConflictAware:
        return (
          <SuggestionLabel
            isDot
            color={loggedInConflictColor}
            text={loggedInConflictText}
          />
        )
      default:
        return null
    }
  }

  return (
    <OutsideWrapper className={wrapperClassName}>
      {!isCollapsed && (
        <CreatorAvatar
          isCollapsed={isCollapsed}
          creatorAvatar={creatorAvatar}
        />
      )}

      <Wrapper
        $isPreview={isPreview}
        $isMap={isMap}
        $isDecidedInPreview={isDecided && isPreview}
        $isCollapsed={isCollapsed}
        $isWithdrawn={isWithdrawn || isPast}
        $suggestionsLength={suggestionsLength}
        $decidedInPopup={isDecidedInPopup}
        onClick={toggleSuggestionPopupHandle}
        className={contentClassName}>
        {!isPreview && !isCollapsed && renderStatusLabel()}

        {renderConflictLabel()}

        {!isCollapsed && <CreatorName>{creatorName}</CreatorName>}

        <MainInfo
          $isWhen={suggestion.type === GAMEPLAN_TYPES.when}
          $isCollapsed={isCollapsed}>
          {isCollapsed && (
            <CreatorAvatar
              isCollapsed={isCollapsed}
              creatorAvatar={creatorAvatar}
            />
          )}

          {/* FIX-4a: expanded Game Plan list rows (isCollapsed) previously showed only the
              avatar with no name -- suggester attribution existed in the data (suggestion.creator,
              now populated by the shim's devSuggestionToBeta) but nothing rendered it here. */}
          {isCollapsed && suggestion.creator?.name && (
            <CreatorNameCollapsed>
              {truncate(creatorName, {
                length: MAX_CREATOR_NAME_LENGTH,
                separator: "...",
              })}
            </CreatorNameCollapsed>
          )}

          {/* {img && <Image src={img} alt={value} />} */}

          {/* AI-CONFORMANCE fix 1 (teeup-create-spec.md "Starred slot", ruling P1): advisory
              star on the top-ranked candidate of an undecided suggestion set -- state only, no
              "AI said so" provenance label (I-4). Never rendered on a decided suggestion (this
              component only reaches here when NOT decided -- see SuggestionsHorizontalList). */}
          {!isDecided && suggestion.isStarred && (
            <StarBadge data-testid="suggestion-star">&#9733;</StarBadge>
          )}

          {renderValues()}
        </MainInfo>

        {isPreviewReactions && (
          <PreviewSuggestionReactions
            suggestionId={suggestion.id}
            reactions={suggestion.reactions}
          />
        )}

        {isMap && children}

        {isRemoveBtn && (
          <RemoveSuggestionBtn
            isCreateTeeup={isCreateTeeup}
            isNoPadding
            isInline
            type={suggestion.type}
            id={isCreateTeeup ? suggestion.slug : suggestion.id}
          />
        )}
      </Wrapper>

      {isSuggestionPopupOpen &&
        ReactDOM.createPortal(
          <SuggestionPopup
            activeTeeup={activeTeeup}
            people={people}
            draggableSuggestionRow={
              <SuggestionItem {...props} isPopup isCollapsed={false} />
            }
            currentSuggestion={suggestion}
            currentSuggestionData={suggestion}
            toggleSuggestionPopup={toggleSuggestionPopupHandle}
          />,
          document.querySelector("#modal-root")
        )}
    </OutsideWrapper>
  )
}

SuggestionItem.propTypes = {
  isCollapsed: PropTypes.bool,
  isPopup: PropTypes.bool,
  isMap: PropTypes.bool,
  suggestion: PropTypes.object,
  suggestionsLength: PropTypes.number,
  isPreview: PropTypes.bool,
  isCreateTeeup: PropTypes.bool,
  isCalendarConflict: PropTypes.bool,
  isReacted: PropTypes.bool,
  isDisableOnClick: PropTypes.bool,
  openSuggestionPreview: PropTypes.func,
  contentClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
}

const mapStateToProps = state => ({
  userInfo: selectUserInfo(state),
  teeupId: selectActiveTeeupId(state),
  activeTeeup: selectActiveTeeup(state),
  people: selectTeeupPeople(state),
  linkedGuestCalendar: selectGuestLinkedCalendar(state),
  isAuthenticated: selectToken(state),
})

export default connect(mapStateToProps)(SuggestionItem)
